import { UzButton, UzInput, UzTabs, type UzTabDefinition } from '@utazzo/ui';
import { Plus } from 'lucide-react';
import React, { useState } from 'react';

import { type FixedTab } from '../page-screen-config';
import { SectionForm } from '../section-forms';
import { sectionLabel } from '../section-presets';

import { SeoPanel } from './SeoPanel';

import type { PageSection } from '../pages.api';
import type { UpdateWebsitePageRequest, WebsitePageDetail } from '@utazzo/shared';

interface Props {
  tabs: FixedTab[];
  /**
   * Top-level groups, in order. When present the tab bar becomes two levels:
   * groups on top, the sections of the active group on the row below. Omit for
   * the short screens - they keep the single flat strip.
   */
  groups?: string[];
  sections: PageSection[];
  meta: WebsitePageDetail | null;
  canWrite: boolean;
  adding: boolean;
  /** Create the section for a tab that has none yet. */
  onAdd: (sectionType: string) => void;
  onSaveTitle: (section: PageSection, title: string | null) => void;
  onSaveSubtitle: (section: PageSection, subtitle: string | null) => void;
  onChangeData: (section: PageSection, next: Record<string, unknown>) => void;
  onSaveSeo: (payload: Partial<UpdateWebsitePageRequest>) => void;
  /** Public path this page type is served under; shown beside the slug field. */
  publicPathPrefix?: string;
}

const SEO_TAB = '__seo__';

/** Amber dot marking a section the page hasn't had added yet. */
const MissingDot = (): React.JSX.Element => (
  <span
    className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400"
    aria-label="Not added yet"
    title="Not added yet"
  />
);

/**
 * Fixed tabbed editor (Destination-page style) for Tourism Boards. Renders the
 * shared <UzTabs> so the tab bar is visually identical to the Destination page:
 * one tab per section of a known layout plus an SEO tab. Each content tab edits
 * a single section - located by `sectionType`, created on demand - through the
 * per-type <SectionForm>. All edits flow through the parent's queue/save.
 *
 * Screens that pass `groups` get a two-level bar instead, so a dozen-plus
 * sections stay readable without the strip running off the editor.
 */
export const FixedTabsEditor = ({
  tabs,
  groups,
  sections,
  meta,
  canWrite,
  adding,
  onAdd,
  onSaveTitle,
  onSaveSubtitle,
  onChangeData,
  onSaveSeo,
  publicPathPrefix,
}: Props): React.JSX.Element => {
  const grouped = (groups?.length ?? 0) > 0;
  const [active, setActive] = useState<string>(tabs[0]?.sectionType ?? SEO_TAB);
  const [activeGroup, setActiveGroup] = useState<string>(groups?.[0] ?? '');
  /** Remembers the section last opened in each group, so switching back returns to it. */
  const [activeInGroup, setActiveInGroup] = useState<Record<string, string>>({});

  const renderSection = (tab: FixedTab): React.ReactNode => {
    // First section matching this tab's type (one section per tab).
    const section = sections.find((s) => s.sectionType === tab.sectionType) ?? null;
    if (!section) {
      return (
        <div className="rounded-md border border-dashed border-(--border-strong) p-8 text-center">
          <p className="text-sm text-(--fg-muted)">
            The “{sectionLabel(tab.sectionType)}” section hasn’t been added to this page yet.
          </p>
          {canWrite && (
            <UzButton
              size="sm"
              variant="secondary"
              className="mt-4"
              loading={adding}
              onClick={() => onAdd(tab.sectionType)}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add this section
            </UzButton>
          )}
        </div>
      );
    }
    return (
      <div>
        <label className="mb-1 block text-xs font-medium text-(--fg-muted)">Heading / title</label>
        <UzInput
          key={`${section.id}-title`}
          defaultValue={section.title ?? ''}
          placeholder="Shown as the section heading where applicable"
          disabled={!canWrite}
          onBlur={(e) => onSaveTitle(section, e.target.value || null)}
        />
        <label className="mt-3 mb-1 block text-xs font-medium text-(--fg-muted)">
          Subheading / description
        </label>
        <UzInput
          key={`${section.id}-subtitle`}
          defaultValue={section.subtitle ?? ''}
          placeholder="Shown under the heading where applicable"
          disabled={!canWrite}
          onBlur={(e) => onSaveSubtitle(section, e.target.value || null)}
        />
        <div className="mt-4 border-t border-(--border) pt-4">
          <SectionForm
            sectionType={section.sectionType}
            data={(section.data as Record<string, unknown>) ?? {}}
            onChange={(next) => onChangeData(section, next)}
            disabled={!canWrite}
          />
        </div>
      </div>
    );
  };

  const isMissing = (tab: FixedTab): boolean =>
    !sections.some((s) => s.sectionType === tab.sectionType);

  const tabDef = (tab: FixedTab): UzTabDefinition => ({
    value: tab.sectionType,
    label: (
      <span className="flex items-center gap-1.5">
        {tab.label}
        {isMissing(tab) && <MissingDot />}
      </span>
    ),
    content: renderSection(tab),
  });

  const seoTab: UzTabDefinition = {
    value: SEO_TAB,
    label: 'SEO',
    content: meta ? (
      <SeoPanel
        page={meta}
        onSave={onSaveSeo}
        publicPathPrefix={publicPathPrefix}
        disabled={!canWrite}
      />
    ) : null,
  };

  if (!grouped) {
    const tabDefs: UzTabDefinition[] = [...tabs.map(tabDef), seoTab];

    return (
      <div className="px-4 py-4 max-sm:px-2">
        {/* Contain the strip: a long tab row scrolls on its own instead of
            dragging the form fields below it off-screen. */}
        <UzTabs
          value={active}
          onValueChange={setActive}
          tabs={tabDefs}
          listClassName="max-w-full overflow-x-auto max-sm:justify-start"
        />
      </div>
    );
  }

  // Two-level bar. The SEO panel joins the last group.
  const groupNames = groups ?? [];
  const lastGroup = groupNames[groupNames.length - 1];
  const entriesFor = (group: string): UzTabDefinition[] => [
    ...tabs.filter((t) => t.group === group).map(tabDef),
    ...(group === lastGroup ? [seoTab] : []),
  ];

  const groupDefs: UzTabDefinition[] = groupNames.map((group) => {
    const entries = entriesFor(group);
    const current = entries.find((e) => e.value === activeInGroup[group]) ?? entries[0];
    return {
      value: group,
      label: (
        <span className="flex items-center gap-1.5">
          {group}
          {tabs.some((t) => t.group === group && isMissing(t)) && <MissingDot />}
        </span>
      ),
      // Second level is the same <UzTabs> as the first - the CRM has exactly
      // one tab pattern and this screen shouldn't invent a second one.
      content: (
        <UzTabs
          value={current?.value}
          onValueChange={(value) => setActiveInGroup((prev) => ({ ...prev, [group]: value }))}
          tabs={entries}
          listClassName="max-w-full overflow-x-auto max-sm:justify-start"
        />
      ),
    };
  });

  return (
    <div className="px-4 py-4">
      <UzTabs
        value={activeGroup}
        onValueChange={setActiveGroup}
        tabs={groupDefs}
        listClassName="max-w-full overflow-x-auto max-sm:justify-start"
      />
    </div>
  );
};
